import { useState } from 'react'
import { updateContact } from '../firebase/contacts'
import { logLinkedInMessage } from '../firebase/activities'
import './LinkedInWorkflow.css'

const LINKEDIN_TEMPLATES = {
  initial: {
    name: 'Initial Connection',
    message: `Hi {{Name}}, 
I work on AI security training and governance, thought it'd be good to connect given your role.`
  },
  followup: {
    name: 'Follow-up Question',
    message: `Quick question, are you already dealing with AI security or governance internally, or still early-stage?`
  },
  value_offer: {
    name: 'Value Offer',
    message: `We built a short AI security readiness assessment, happy to share if useful.`
  }
}

const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started', color: '#6b7280' },
  { value: 'initial_sent', label: 'Initial Sent', color: '#3b82f6' },
  { value: 'followup_sent', label: 'Follow-up Sent', color: '#8b5cf6' },
  { value: 'replied', label: 'Replied', color: '#10b981' },
  { value: 'value_offered', label: 'Value Offered', color: '#f59e0b' },
  { value: 'converted', label: 'Converted', color: '#059669' }
]

const LinkedInWorkflow = ({ contact, userId, onUpdate }) => {
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [customMessage, setCustomMessage] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)

  const currentStatus = contact.linkedinStatus || 'not_started'
  const statusInfo = STATUS_OPTIONS.find(s => s.value === currentStatus) || STATUS_OPTIONS[0]

  const replacePlaceholders = (template) => {
    const firstName = contact.name?.split(' ')[0] || contact.name || 'there'
    return template.replace(/\{\{Name\}\}/g, firstName)
  }

  const handleCopyMessage = async (templateKey, autoLog = false) => {
    const template = LINKEDIN_TEMPLATES[templateKey]
    if (!template) return

    const message = replacePlaceholders(template.message)
    
    try {
      await navigator.clipboard.writeText(message)
      
      if (autoLog) {
        // Auto-log the message and update status
        await handleSendMessage(templateKey, message)
      } else {
        alert('Message copied to clipboard!')
      }
    } catch (error) {
      console.error('Failed to copy:', error)
      alert('Failed to copy message. Please try again.')
    }
  }

  const handleSendMessage = async (templateKey, message) => {
    setIsUpdating(true)
    try {
      // Log the LinkedIn message activity
      await logLinkedInMessage(contact.id, userId, {
        subject: LINKEDIN_TEMPLATES[templateKey].name,
        description: message
      })

      // Update LinkedIn status based on template
      let newStatus = currentStatus
      if (templateKey === 'initial') {
        newStatus = 'initial_sent'
      } else if (templateKey === 'followup') {
        newStatus = 'followup_sent'
      } else if (templateKey === 'value_offer') {
        newStatus = 'value_offered'
      }

      if (newStatus !== currentStatus) {
        await updateContact(contact.id, { linkedinStatus: newStatus })
        onUpdate({ ...contact, linkedinStatus: newStatus })
      }

      alert('Message logged and status updated!')
    } catch (error) {
      console.error('Error sending message:', error)
      alert('Failed to log message. Please try again.')
    } finally {
      setIsUpdating(false)
      setShowTemplateModal(false)
    }
  }

  const handleStatusChange = async (newStatus) => {
    setIsUpdating(true)
    try {
      await updateContact(contact.id, { linkedinStatus: newStatus })
      onUpdate({ ...contact, linkedinStatus: newStatus })
    } catch (error) {
      console.error('Error updating status:', error)
      alert('Failed to update status. Please try again.')
    } finally {
      setIsUpdating(false)
    }
  }

  const getNextAction = () => {
    switch (currentStatus) {
      case 'not_started':
        return { template: 'initial', label: 'Send Initial Message' }
      case 'initial_sent':
        return { template: 'followup', label: 'Send Follow-up' }
      case 'followup_sent':
      case 'replied':
        return { template: 'value_offer', label: 'Offer Value' }
      default:
        return null
    }
  }

  const nextAction = getNextAction()

  return (
    <div className="linkedin-workflow">
      <div className="workflow-header">
        <h3>💼 LinkedIn Outreach</h3>
        <div className="status-badge" style={{ backgroundColor: statusInfo.color + '20', color: statusInfo.color }}>
          {statusInfo.label}
        </div>
      </div>

      <div className="workflow-content">
        {nextAction && (
          <div className="quick-action">
            <button
              className="btn-primary workflow-btn"
              onClick={() => handleCopyMessage(nextAction.template, true)}
              disabled={isUpdating}
            >
              {isUpdating ? 'Processing...' : `📋 ${nextAction.label} & Log`}
            </button>
            <span className="action-hint">Copies message and logs activity</span>
          </div>
        )}

        <div className="template-actions">
          <h4>Message Templates</h4>
          <div className="template-buttons">
            <button
              className="template-btn"
              onClick={() => handleCopyMessage('initial')}
              disabled={isUpdating}
            >
              📋 Initial Connection
            </button>
            <button
              className="template-btn"
              onClick={() => handleCopyMessage('followup')}
              disabled={isUpdating}
            >
              📋 Follow-up Question
            </button>
            <button
              className="template-btn"
              onClick={() => handleCopyMessage('value_offer')}
              disabled={isUpdating}
            >
              📋 Value Offer
            </button>
          </div>
        </div>

        <div className="status-controls">
          <h4>Update Status</h4>
          <select
            className="status-select"
            value={currentStatus}
            onChange={(e) => handleStatusChange(e.target.value)}
            disabled={isUpdating}
          >
            {STATUS_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="workflow-timeline">
          <div className={`timeline-step ${currentStatus === 'not_started' ? 'active' : currentStatus !== 'not_started' ? 'completed' : ''}`}>
            <div className="step-indicator"></div>
            <div className="step-content">
              <strong>Initial Connection</strong>
              <p>Send connection request message</p>
            </div>
          </div>
          <div className={`timeline-step ${currentStatus === 'initial_sent' ? 'active' : ['followup_sent', 'replied', 'value_offered', 'converted'].includes(currentStatus) ? 'completed' : ''}`}>
            <div className="step-indicator"></div>
            <div className="step-content">
              <strong>Follow-up (Day 1-3)</strong>
              <p>Ask about their AI security situation</p>
            </div>
          </div>
          <div className={`timeline-step ${currentStatus === 'replied' ? 'active' : ['value_offered', 'converted'].includes(currentStatus) ? 'completed' : ''}`}>
            <div className="step-indicator"></div>
            <div className="step-content">
              <strong>Offer Value</strong>
              <p>Share assessment or relevant resource</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LinkedInWorkflow

